define((require) => {
    "use strict";

    const _                  = require("underscore");
    const $                  = require("jquery");
    const AbstractWidgetView = require("shared/ui/experience/widgets/AbstractWidgetView");
    const channelBridge      = require("shared/ui/viewer/ChannelBridge");
    const EmbedMixin         = require("shared/ui/experience/widgets/embedWidget/EmbedMixin");
    const UrlUtil            = require("shared/util/UrlUtil");
    const Window             = require("shared/services/Window");
    const Browser            = require("shared/services/Browser");

    /**
     * The view rendering an embed widget.
     *
     * @class EmbedWidgetView
     * @constructor
     *
     * @cfg {EmbedWidgetModel} model The model of the embed widget being rendered.
     */
    return AbstractWidgetView.extend("EmbedWidgetView", { // TODO: consider retuning a promise from .promise() that resolves when the embedded Experience/Group is resolved,
        //       should probably be configurable per widget...

        mixin: EmbedMixin,

        tagName: "div",

        className: `${AbstractWidgetView.prototype.className} embed-widget`,

        _channel: null,
        _descriptor: null,

        _embedElement: null,
        _sizingElement: null, // iframe on direct url, matched element on embedded via channels!
        _activeState: null,

        /**
         * @private @type {Boolean}
         * Set to true if the embedding via channels failed for some obscure reason, thus defaulting to the
         * direct url (also on re-render).
         */
        _failedToEmbed: false,

        updateRendering(element, bounds, active, edited) {
            const staticRepresentation = !this.options.enableAnimations;
            const rendering            = this._activeState === null; // first render only, also for adjacent Scene rendering
            const becameInactive       = !active && (this._activeState || rendering);

            let becameActive = active && !this._activeState;

            this._activeState = active;

            // For the editor and static (thumbnail) state
            if (edited || staticRepresentation) {
                element.empty();

                this._sizingElement = this._embedElement = $("<div class='placeholder'/>").appendTo(element);

            // When inside the viewer/player
            } else {
                this._descriptor = UrlUtil.getUrlDescriptor(this.model.get("url"));

                let embed    = true; // since we want direct url behaviour in editor as well
                const bridge = !this._failedToEmbed && channelBridge();
                const url    = this._descriptor.type === "url";
                const scene  = this.viewScope.getSceneNumber(); // current scene number in *this* Experience
                let iframeUrl; // some external link or perhaps a direct url for a group/experience

                // Viewer is embedded via a Channel, so we can utilise this to ensure we can embed another
                // Experience or Group that can do cross domain JS calls because of the neatness of channels...
                if (bridge && !url) {
                    this._descriptor.sameOrigin = true; // for consistency
                    if (!this._sizingElement) { // since embedElement can only be set after population
                        element.empty();
                        this._channel = bridge.embed({
                            scene,
                            descriptor:  this._descriptor,
                            element:     $("<div/>").appendTo(element),
                            callback:    (populated) => {
                                const channelContainer = this._channel.container(); // container = iframe
                                this._failedToEmbed = !populated;

                                if (!populated || !document.body.contains(channelContainer)) {
                                    // If the embedding via channels somehow fails, default to a direct url...
                                    bridge.removeEmbed(this._channel, true); // true = destroy
                                    this._sizingElement = null;
                                    this.render();
                                } else {
                                    // We can now reference the iframe, i.e. actual embed element, and so we must
                                    // remember to set the state we have now...
                                    this._embedElement = $(channelContainer);
                                    this._childOptions(this._activeState, bounds);
                                }
                            }
                        });
                        // Cannot set "embedElement" at this point, the channel will not have been embedded yet, but
                        // we can set the sizingElement just fine...
                        this._sizingElement = $(this._channel.element()); // matched element
                    }
                    embed = false;
                }

                // Not embedded via a Channel, so simply embed using a direct url. The descriptor type can still
                // be any old plain url, it does not have to be an embedded Experience at this point...
                if (embed) {
                    iframeUrl = UrlUtil.getEmbedURL(this._descriptor, "embedded", scene); // can still be cross domain in the Editor!

                    if (!this._sizingElement) {
                        // Disable scrolling on iOS (capabilities.isIframeScrollingSupported) for embedded experiences - due to
                        // the way mobile Safari handles iframes, see discussion
                        // here: http://stackoverflow.com/questions/23083462/how-to-get-an-iframe-to-be-responsive-in-ios-safari
                        const scrolling = Browser.capabilities.isIframeScrollingSupported || url;

                        const frameAttributes = {
                            src:       iframeUrl,
                            scrolling: scrolling ? "auto" : "no"
                        };

                        const title = this.model.get("title");
                        if (title && title.length) {
                            frameAttributes.title = title;
                        }

                        this._sizingElement = this._embedElement = this._createIframe(
                            this._descriptor,
                            frameAttributes
                        ).appendTo(element);
                    }
                }

                // Keep Experiences/Groups embedded via a direct url or urls for the same (client) origin,
                // but potentially not anything else; the embedded Experiences are handled in the else-block below when
                // on same domain (Viewer), but the Editor will be here for top most Viewers and embedded Experiences...
                if (url || !this._descriptor.sameOrigin) { // OR part for Editor top level preview embedding Viewers via direct url
                    // We only need to do anything on active if we have a blanked url...
                    becameActive = becameActive && (this._embedElement.attr("src") !== iframeUrl);

                    // The widget can become inactive in two ways: on the same Scene, e.g. because of scroll or
                    // resize, or because of Scene navigation or adjacent Scene rendering.
                    const viewModel = this._viewModel();
                    // eslint-disable-next-line eqeqeq
                    let type = viewModel.get("currentScene") == scene ? "Scroll" : "Navigate";  // == intentional, string/number

                    // Only on first rendering (not becoming inactive/active) or on special case when navigating
                    // between different tabs in the browser, we coerce a blanking of the src tab to ensure we always
                    // stops embedded videos and what not. This will be the case if we are rendering because of a
                    // "change:shown" event that will have triggered an "change:active" event...
                    if (!viewModel.get("shown")) { // TODO: this logic should eventually also be available for Lightboxes.
                        type = "Hidden";
                    } else if (rendering) {
                        type = "Render";
                    }

                    // Three attributes/states controls how the iframe src is blanked or not:
                    //
                    // - "blankOnRender":   blank when rendered the first time on an adjacent Scene, e.g. if we
                    //                      no the url links to a video player that will auto play. Default
                    //                      false.
                    // - "blankOnNavigate": blank when navigating away from the Scene, e.g. to stop a video
                    //                      when the Scene is hidden. Default true. If true and "blankOnRender"
                    //                      is false, the iframe will will be inserted again in the background
                    //                      while the Scene hidden, to prepare for navigation while the Scene
                    //                      is still rendered in memory.
                    // - "blankOnHidden":   blank when navigating away from the tab in the browser, e.g. to stop a video
                    //                      when the tab containing the Experience is hidden. Default true. If true
                    //                      and "blankOnRender" is false, the iframe will will be inserted again in
                    //                      the background while the tab is still hidden, to prepare for the user
                    //                      clicking back to the tab in a while.
                    // - "blankOnScroll:    blank when the user scrolls/resize out of focus. Default true.
                    //
                    // Currently, no models have these properties, so they all default; eventually we can make
                    // the Editor support this for fine grained control over this behaviour.

                    let blank = this.model.get(`blankOn${type}`);

                    // Support legacy widget data with no such properties and/or to do a sensible default...
                    // eslint-disable-next-line eqeqeq
                    if (blank == null) {
                        // Default: blank on all except initial rendering to maintain backwards compatibility... :/
                        blank = type !== "Render";
                    }

                    // Same domain url, // e.g. a client linking to a page on its own domain...
                    if (this._descriptor.sameOrigin) {
                        if (becameActive || (rendering && !blank)) {
                            // Same origin, so safe to sniff the document title for potential error code; this will not
                            // break embed functionality, we just want to report it so we can track it in LogStash...
                            this._embedElement.one("load", () => {
                                this._statusCode(this._embedElement[0].contentWindow);
                            });
                        }

                    // Cross domain url...
                    } else {
                        const updateUrl = (clear) => {
                            const url = clear ? "about:blank" : iframeUrl;
                            this._log().verbose(`Setting embed url: '${url}'`, this);
                            this._embedElement.attr("src", url);
                        };

                        if (becameActive) {
                            updateUrl();
                        }
                        if (becameInactive) {
                            if (blank) {
                                updateUrl(true);

                                // If we blanked because of navigation, prepare for to insert the url again when
                                // hidden (now, just in the immediate future are the dom update) if we should not
                                // blank on render. This ensures the iframe contents will be as ready as they can
                                // be for when the user navigates back to this Scene again while this Scene instance
                                // is still buffered/rendered...
                                if (url && (type === "Navigate" || type === "Hidden")) { // url check to avoid restart embedded Experiences again in the Editor
                                    if (!this.model.get("blankOnRender")) { // also work for default!
                                        _.defer(updateUrl);
                                    }
                                }
                            }
                        }
                    }

                // Make sure we tell the embedded Experience about its new state, including all its embedded
                // children recursively. If we do not do this, it will continue auto playing while not shown...
                } else if (this._embedElement) {
                    this._childOptions(this._activeState, bounds);
                }
            }

            this._sizingElement.css({
                width:     "1px",
                minWidth:  "100%",
                height:    "1px",
                minHeight: "100%"
            });
        },

        /**
         * @private
         */
        _childOptions(active, bounds) {
            // We cannot simply set the state on this Viewer, since that will also affect siblings
            // (e.g. on other scenes), which we do not want, but by calling the specific child, it will
            // recurse down from there (as opposed to the top most Viewer) because we specify {recursive: true}
            // directly. And, we set "active, not just "stalled", as we want timers and what not to be stopped
            // while the embedded Experience is for example hidden on a non-visible adjacent Scene...
            const iframe = this._embedElement[0];
            const childState = Window.viewer(iframe, "state"); // iframe.contentWindow may not be available yet or even cross domain in Editor!
            const state = {name: "active", value: active, recursive: true};

            // Can we talk directly to the child Viewer? If so, it has already been primed with the options
            // sometime in the past and we are only concerned about state changes henceforth as its size, for example,
            // will be handled by its own ResponsibleViewerController...
            if (childState) {
                childState(state); // same origin if available!

            // We do not have a child viewer yet, so save the state and options for when it comes online in a bit. When
            // the embedded child viewer registers it self at this (parent) viewer, it will receive the options and
            // state stored here, which the embedded Viewer will then use during its initialisation routine. In the
            // Editor, however, we will not have a viewer proxy due to cross domain issues, i.e. the viewer being
            // inserted directly into a pre-existing dom element...
            } else {
                Window.viewer("self", "register", iframe, {state, initialSize: bounds.toSize()});
            }
        },

        onClose() {
            // If we have a channels, it means we have an embedded Experience/Group via an temp in-place channel that
            // when removed will close the Viewer recursively (will be anything by null in the editor)...
            if (this._channel) {
                const bridge = channelBridge();
                if (bridge) {
                    bridge.removeEmbed(this._channel);
                    this._channel = null;
                }

            // Remember to close embedded Viewer recursively for direct urls manually, if any, as we do not have
            // an in-place Channel to call the Viewer proxy for us...
            } else if (this._embedElement && this._descriptor && this._descriptor.type !== "url") { // "descriptor" can be null in editor
                Window.viewer(this._embedElement[0], "state", {name: "closed", value: true}); // will recurse
            }

            this.__super();
        }
    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/embedWidget/EmbedWidgetView.js